import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { IconUser } from "@tabler/icons-react";
import { GetCountries, GetState } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";
import Select from "react-select"; // Import react-select
import Spinner from "../ogComponent/spinner";

export default function EditStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      location_state: "",
      selectedCourse: [],
      selectedBatch: [],
    },
  });

  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [student, setStudent] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countryId, setCountryId] = useState(null);
  const [stateId, setStateId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const statusOptions = ["Active", "In-Active"];
  const standardOptions = ["8th", "9th", "SSLC", "Plus One", "Plus Two", "Degree", "Diploma", "PG", "Others"];
  const token = localStorage.getItem("accessToken");

  const selectedCourses = watch("selectedCourse"); // Watch selected courses

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        if (!token) {
          toast.error("Unauthorized: Access token missing");
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_API}/api/course/students/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setStudent(response.data);
        setProfilePhotoPreview(response.data.profile_photo || "/assets/user.png");

        // Set form values
        setValue("student_code", response.data.student_code);
        setValue("full_name", response.data.full_name);
        setValue("email", response.data.email);
        setValue("contact_number", response.data.contact_number);
        setValue("whatsapp_number", response.data.whatsapp_number);
        setValue("aadhar_number", response.data.aadhar_number);
        setValue("location_country", response.data.location_country);
        setValue("location_state", response.data.location_state);
        setValue("location_city", response.data.location_city);
        setValue("standard", response.data.standard);
        setValue("school", response.data.school);
        setValue("medium", response.data.medium);
        setValue("parent_name", response.data.parent_name);
        setValue("parent_relation", response.data.parent_relation);
        setValue("parent_phone_no", response.data.parent_phone_no);
        setValue("parent_whatsapp_no", response.data.parent_whatsapp_no);
        setValue("status", response.data.status === true ? "Active" : "In-Active");

        // Set initial selected courses and batches
        const initialCourses = response.data.selected_course.map((course) => ({
          value: course.id,
          label: course.course_name,
        }));
        const initialBatches = response.data.selected_batch.map((batch) => ({
          value: batch.id,
          label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
          courseId: batch.course.id,
        }));
        setValue("selectedCourse", initialCourses);
        setValue("selectedBatch", initialBatches);

        trigger();
      } catch (error) {
        toast.error("Failed to fetch student data");
        console.error("Error:", error);
      }
    };

    const fetchCourses = async () => {
      try {
        if (!token) {
          toast.error("Unauthorized: Access token missing");
          return;
        }

        const response = await axios.get(`${process.env.REACT_APP_API}/api/course/combined-courses/`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCourses(response.data);
      } catch (error) {
        toast.error("Failed to fetch courses");
        console.error("Error:", error);
      }
    };

    GetCountries().then((result) => {
      setCountries(result);
    });

    fetchStudent();
    fetchCourses();
  }, [id, setValue, token]);

  useEffect(() => {
    const fetchBatchesByCourses = async () => {
      try {
        if (!selectedCourses || selectedCourses.length === 0) {
          setBatches([]);
          setValue("selectedBatch", []);
          return;
        }

        const courseIds = selectedCourses.map((course) => course.value).join(",");
        const response = await axios.get(`${process.env.REACT_APP_API}/api/course/batches/?course_id=${courseIds}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const fetchedBatches = response.data.map((batch) => ({
          value: batch.id,
          label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
          courseId: batch.course.id,
        }));

        setBatches(response.data);

        // Automatically select batches corresponding to the selected courses
        const newSelectedBatches = fetchedBatches.filter((batch) =>
          selectedCourses.some((course) => course.value === batch.courseId)
        );
        setValue("selectedBatch", newSelectedBatches);
      } catch (error) {
        console.error("Error fetching batches:", error);
        toast.error("Failed to fetch batches");
        setBatches([]);
        setValue("selectedBatch", []);
      }
    };

    if (token) fetchBatchesByCourses();
  }, [token, selectedCourses, setValue]);

  useEffect(() => {
    if (student && countries.length > 0) {
      const country = countries.find((c) => c.name === student.location_country);
      if (country) {
        setCountryId(country.id);
        GetState(country.id)
          .then((stateResult) => {
            setStates(stateResult);
            const state = stateResult.find((s) => s.name === student.location_state);
            if (state) {
              setStateId(state.id);
              setValue("location_state", state.name);
            }
          })
          .catch((error) => {
            console.error("Error fetching states:", error);
            setStates([]);
          });
      }
    }
  }, [student, countries, setValue]);

  const handleCountryChange = (e) => {
    const selectedCountryId = parseInt(e.target.value);
    setCountryId(selectedCountryId);
    setStateId(null);
    const country = countries.find((c) => c.id === selectedCountryId);
    if (country) setValue("location_country", country.name);
    setValue("location_state", "");
    setValue("location_city", "");
    if (selectedCountryId) {
      GetState(selectedCountryId)
        .then((result) => setStates(result))
        .catch((error) => {
          console.error("Error fetching states:", error);
          setStates([]);
        });
    } else {
      setStates([]);
    }
  };

  const handleStateChange = (e) => {
    const selectedStateId = parseInt(e.target.value);
    setStateId(selectedStateId);
    const state = states.find((s) => s.id === selectedStateId);
    if (state) setValue("location_state", state.name);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setProfilePhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    if (Object.keys(errors).length > 0) {
      toast.error("Please fill all required fields");
      return;
    }

    if (!token) {
      toast.error("Unauthorized: Access token missing");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("student_code", data.student_code);
    formData.append("full_name", data.full_name);
    formData.append("email", data.email || "");
    formData.append("contact_number", data.contact_number);
    formData.append("whatsapp_number", data.whatsapp_number);
    formData.append("aadhar_number", data.aadhar_number || "");
    formData.append("location_city", data.location_city);
    formData.append("location_state", data.location_state);
    formData.append("location_country", data.location_country);
    formData.append("standard", data.standard);
    formData.append("school", data.school);
    formData.append("medium", data.medium);
    formData.append("parent_name", data.parent_name);
    formData.append("parent_relation", data.parent_relation);
    formData.append("parent_phone_no", data.parent_phone_no);
    formData.append("parent_whatsapp_no", data.parent_whatsapp_no);
    formData.append("status", data.status === "Active" ? true : false);

    // Append multiple course and batch IDs
    data.selectedCourse.forEach((course, index) => {
      formData.append(`selected_course_id`, course.value);
    });
    data.selectedBatch.forEach((batch, index) => {
      formData.append(`selected_batch_id`, batch.value);
    });

    if (profilePhotoFile) formData.append("profile_photo", profilePhotoFile);

    try {
      await axios.put(`${process.env.REACT_APP_API}/api/course/students/${id}/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Student updated successfully!");
      navigate("/user/listing");
    } catch (error) {
      if (error.response?.data?.aadhar_number) {
        toast.error("A student with this Aadhar number already exists.");
      } else {
        toast.error(error.response?.data?.message || "Failed to update student");
      }
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!student) return <Spinner />;

  // Options for react-select
  const courseOptions = courses.map((course) => ({
    value: course.id,
    label: course.course_name,
  }));

  return (
    <div className="container py-4 px-5">
      <ToastContainer position="top-right" autoClose={3000} />
      <h2 className="mb-4">Edit Student</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label small mb-0">Profile Photo</label>
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle overflow-hidden"
                style={{ width: "100px", height: "100px", position: "relative", border: "1px solid #ddd" }}
              >
                {profilePhotoPreview ? (
                  <img
                    src={profilePhotoPreview}
                    alt="Profile Preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    <IconUser size={40} color="#666" />
                  </div>
                )}
              </div>
              <input
                type="file"
                id="profilePhoto"
                style={{ display: "none" }}
                onChange={handleFileChange}
                accept="image/*"
              />
              <button
                type="button"
                className="btn btn-secondary ms-3"
                onClick={() => document.getElementById("profilePhoto").click()}
              >
                Select File
              </button>
            </div>
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Full Name</label>
            <input
              type="text"
              style={{ backgroundColor: "#00000010" }}
              className="form-control border-2"
              {...register("full_name", { required: "Full Name is required" })}
            />
            {errors.full_name && <span className="text-danger">{errors.full_name.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Student Code</label>
            <input
              type="text"
              style={{ backgroundColor: "#00000010" }}
              className="form-control border-2"
              {...register("student_code", { required: "Student Code is required" })}
              disabled
            />
            {errors.student_code && <span className="text-danger">{errors.student_code.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Email Address</label>
            <input
              type="email"
              style={{ backgroundColor: "#00000010" }}
              className="form-control border-2"
              {...register("email")}
            />
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Contact Number</label>
            <input
              type="number"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("contact_number", { required: "Contact Number is required" })}
            />
            {errors.contact_number && <span className="text-danger">{errors.contact_number.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">WhatsApp Number</label>
            <input
              type="number"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("whatsapp_number", { required: "WhatsApp Number is required" })}
            />
            {errors.whatsapp_number && <span className="text-danger">{errors.whatsapp_number.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Aadhar Number</label>
            <input
              type="text"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("aadhar_number", {
                pattern: { value: /^[0-9]{12}$/, message: "Aadhar must be 12 digits" },
              })}
            />
            {errors.aadhar_number && <span className="text-danger">{errors.aadhar_number.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Country</label>
            <select
              className="form-select border-2"
              style={{ backgroundColor: "#00000010" }}
              onChange={handleCountryChange}
              value={countryId || ""}
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            <input
              type="hidden"
              {...register("location_country", { required: "Country is required" })}
            />
            {errors.location_country && <span className="text-danger">{errors.location_country.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">State</label>
            <select
              className="form-select border-2"
              style={{ backgroundColor: "#00000010" }}
              onChange={handleStateChange}
              value={stateId || ""}
              disabled={!countryId}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
            <input
              type="hidden"
              {...register("location_state", { required: "State is required" })}
            />
            {errors.location_state && <span className="text-danger">{errors.location_state.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">City</label>
            <input
              type="text"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("location_city", { required: "City is required" })}
            />
            {errors.location_city && <span className="text-danger">{errors.location_city.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Standard</label>
            <select
              className="form-select border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("standard", { required: "Standard is required" })}
            >
              <option value="">Select Standard</option>
              {standardOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.standard && <span className="text-danger">{errors.standard.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">School</label>
            <input
              type="text"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("school", { required: "School is required" })}
            />
            {errors.school && <span className="text-danger">{errors.school.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Medium</label>
            <select
              className="form-select border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("medium", { required: "Medium is required" })}
            >
              <option value="">Select Medium</option>
              <option value="English">English</option>
              <option value="Malayalam">Malayalam</option>
            </select>
            {errors.medium && <span className="text-danger">{errors.medium.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Parent Name</label>
            <input
              type="text"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("parent_name", { required: "Parent Name is required" })}
            />
            {errors.parent_name && <span className="text-danger">{errors.parent_name.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Parent Relation</label>
            <input
              type="text"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("parent_relation", { required: "Parent Relation is required" })}
            />
            {errors.parent_relation && <span className="text-danger">{errors.parent_relation.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Parent Phone No</label>
            <input
              type="number"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("parent_phone_no", { required: "Parent Phone is required" })}
            />
            {errors.parent_phone_no && <span className="text-danger">{errors.parent_phone_no.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Parent WhatsApp No</label>
            <input
              type="number"
              className="form-control border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("parent_whatsapp_no", { required: "Parent WhatsApp is required" })}
            />
            {errors.parent_whatsapp_no && <span className="text-danger">{errors.parent_whatsapp_no.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label small mb-0">Status</label>
            <select
              className="form-select border-2"
              style={{ backgroundColor: "#00000010" }}
              {...register("status", { required: "Status is required" })}
            >
              <option value="">Select Status</option>
              {statusOptions.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.status && <span className="text-danger">{errors.status.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label mb-0 small">Courses</label>
            <Select
              isMulti
              options={courseOptions}
              value={selectedCourses}
              onChange={(selected) => setValue("selectedCourse", selected)}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select Courses"
            />
            {errors.selectedCourse && <span className="text-danger">{errors.selectedCourse.message}</span>}
          </div>

          <div className="col-md-4 col-12">
            <label className="form-label mb-0 small">Batches</label>
            <Select
              isMulti
              options={batches.map((batch) => ({
                value: batch.id,
                label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
                courseId: batch.course.id,
              }))}
              value={watch("selectedBatch")}
              isDisabled // Make batch field non-editable
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Batches will be auto-selected"
            />
            {errors.selectedBatch && <span className="text-danger">{errors.selectedBatch.message}</span>}
          </div>

          <div className="mt-4 w-100 d-flex justify-content-end">
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}